import axios from 'axios';
import { api } from './api.js';
import type { ApiResponse, WireEntityDefinition, WindowSpec } from '@elmes/shared';
import type { CosmeticOverlay } from '../modules/desktop/composer/cosmeticOverlay.js';

interface UiOverlayResponse {
  windowKey: string;
  overlay: CosmeticOverlay;
}

/**
 * A user's SAVED personal view (gh#1199 PR-2/PR-3). Mirror of the API
 * `UserViewSpecRow` — `spec` is a full, structurally-valid WindowSpec.
 */
export interface UserViewSpec {
  id: number;
  userId: number;
  entitySlug: string;
  name: string;
  spec: WindowSpec;
  createdAt: string;
  updatedAt: string;
}

/** Create payload for a personal view (spec pre-compiled client-side). */
export interface CreateUserViewInput {
  entitySlug: string;
  name: string;
  spec: WindowSpec;
}

/**
 * Thrown when a create/rename collides with an existing (slug, name) for the user
 * — the server returns 409. Surfaced as a typed error so the UI can show a
 * name-conflict message instead of a generic failure.
 */
export class UserViewConflictError extends Error {
  constructor(message = 'Bu isimde bir kişisel görünüm zaten var') {
    super(message);
    this.name = 'UserViewConflictError';
  }
}

/**
 * Entity OS web service (Phase-1, #421/#427).
 *
 * Thin client for the read-only entity-definition lookup. Project CRUD itself
 * goes through the AI tool gateway (aiService.executeTool('entity.project.*')),
 * not through this service — the gateway carries the dispatch PEP.
 */
export const entityService = {
  /** GET /api/entity/definitions/:slug → the registered definition (the published WIRE shape). */
  async getDefinition(slug: string): Promise<WireEntityDefinition> {
    const { data } = await api.get<ApiResponse<WireEntityDefinition>>(
      `/entity/definitions/${encodeURIComponent(slug)}`,
    );
    if (!data.success || !data.data) {
      throw new Error(data.error || `entity definition "${slug}" not found`);
    }
    return data.data;
  },

  /** GET /api/entity/definitions → all registered EntityDefinitions. */
  async listDefinitions(): Promise<WireEntityDefinition[]> {
    const { data } = await api.get<ApiResponse<WireEntityDefinition[]>>('/entity/definitions');
    return data.data ?? [];
  },

  /**
   * GET /api/entity/ui-overlays/:windowKey → the acting user's cosmetic overlay.
   *
   * Returns an empty overlay `{}` when none is stored (or on a transient read
   * error) — the overlay is purely presentational, so a missing/failed fetch must
   * degrade gracefully to the base spec, never block the form.
   */
  async getUiOverlay(windowKey: string): Promise<CosmeticOverlay> {
    try {
      const { data } = await api.get<ApiResponse<UiOverlayResponse>>(
        `/entity/ui-overlays/${encodeURIComponent(windowKey)}`,
      );
      return data.data?.overlay ?? {};
    } catch {
      return {};
    }
  },

  /** PUT /api/entity/ui-overlays/:windowKey → upsert the acting user's overlay. */
  async saveUiOverlay(windowKey: string, overlay: CosmeticOverlay): Promise<CosmeticOverlay> {
    const { data } = await api.put<ApiResponse<UiOverlayResponse>>(
      `/entity/ui-overlays/${encodeURIComponent(windowKey)}`,
      { overlay },
    );
    return data.data?.overlay ?? {};
  },

  // ─── Personal views (gh#1199 PR-2/PR-3) — self-scoped CRUD ──────────────────

  /** GET /api/entity/user-views?slug= → the acting user's saved views for a slug. */
  async listUserViews(slug: string): Promise<UserViewSpec[]> {
    const { data } = await api.get<ApiResponse<UserViewSpec[]>>('/entity/user-views', {
      params: { slug },
    });
    return data.data ?? [];
  },

  /** GET /api/entity/user-views/:id → one saved view (throws on 404). */
  async getUserView(id: number): Promise<UserViewSpec> {
    const { data } = await api.get<ApiResponse<UserViewSpec>>(
      `/entity/user-views/${id}`,
    );
    if (!data.success || !data.data) {
      throw new Error(data.error || `personal view ${id} not found`);
    }
    return data.data;
  },

  /**
   * POST /api/entity/user-views → create a personal view for the acting user.
   * A duplicate (slug, name) 409 is re-thrown as {@link UserViewConflictError}.
   */
  async createUserView(input: CreateUserViewInput): Promise<UserViewSpec> {
    try {
      const { data } = await api.post<ApiResponse<UserViewSpec>>(
        '/entity/user-views',
        input,
      );
      if (!data.success || !data.data) {
        throw new Error(data.error || 'personal view create failed');
      }
      return data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const msg = (err.response.data as ApiResponse<never> | undefined)?.error;
        throw new UserViewConflictError(msg || undefined);
      }
      throw err;
    }
  },
};
